/**
 * Add NX site: every NX server of one site set up in one go (owner,
 * 2026-10-20, after Moorebank's three servers took an hour one at a time
 * and came out wrong). One login, picked or typed here, becomes the
 * monitoring login of every server whose agent reports the site, the
 * elevated login too if wanted, and auto-add goes on if asked. One request,
 * all or nothing (PUT /api/nx-sites/:siteId/setup).
 *
 * Then it stays open and watches: each agent picks the login up at its next
 * report and says whether NX took it, so the list below goes none → ok, or
 * says exactly which server NX refused.
 */

import { Check, Save } from 'lucide-react';
import { useEffect, useState } from 'react';

import { ApiError, api } from './api.ts';
import { Banner, Button, Drawer, Field } from './components/index.ts';
import { loginSummary, siteState, type NxSite, type SiteHost } from './nx-logins.ts';

interface CredentialOption {
  id: string;
  label: string;
  kind: string;
  username: string | null;
}

interface SetupResult {
  credentialId: string;
  credentialLabel: string;
  servers: Array<{ id: string; label: string }>;
  autoAdd?: { on: boolean; added: unknown[]; held: Array<{ name: string }>; skipped: string[] };
}

const failure = (err: unknown): string => (err instanceof ApiError ? err.message : String(err));
const labelOf = (h: SiteHost): string => h.code ?? h.name;

/** The agent has tried the login the host holds now, and NX took it. */
const loggedIn = (h: SiteHost): boolean =>
  h.nx?.login === 'ok' && h.nxLogins.monitoring !== null && (h.nx.loginUser == null || h.nx.loginUser === '' || h.nx.loginUser === h.nxLogins.monitoring.username);

export function NxSiteSetupDrawer<T extends SiteHost>({
  site,
  onClose,
  refresh,
}: {
  /** Recomputed by the parent from each fresh hosts list, so the servers below move as agents report. */
  site: NxSite<T>;
  onClose: () => void;
  /** Reloads the hosts list; called after saving and then every ten seconds while watching. */
  refresh: () => Promise<void>;
}): React.JSX.Element {
  const [credentials, setCredentials] = useState<CredentialOption[] | null>(null);
  const [mode, setMode] = useState<'existing' | 'new'>('existing');
  const [existing, setExisting] = useState(site.hosts.find((h) => h.nxLogins.monitoring !== null)?.nxLogins.monitoring?.credentialId ?? '');
  const [label, setLabel] = useState(`${site.name} NX monitoring`);
  const [username, setUsername] = useState('');
  const [secret, setSecret] = useState('');
  const [elevated, setElevated] = useState<'none' | 'same' | string>('none');
  // Auto-add is opt-in per site and stays off unless ticked (owner, 2026-10-20).
  const [autoAdd, setAutoAdd] = useState(site.autoAdd);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SetupResult | null>(null);

  useEffect(() => {
    api
      .get<{ credentials: CredentialOption[] }>('/api/credentials')
      .then((r) => {
        const userpass = r.credentials.filter((c) => c.kind === 'userpass');
        setCredentials(userpass);
        if (userpass.length === 0) setMode('new');
      })
      .catch((err: unknown) => setError(failure(err)));
  }, []);

  // Watching: once saved, ask again every ten seconds until every server logs in.
  const allIn = site.hosts.every(loggedIn);
  useEffect(() => {
    if (result === null || allIn) return;
    const t = window.setInterval(() => void refresh(), 10_000);
    return () => window.clearInterval(t);
  }, [result, allIn, refresh]);

  const ready = site.siteId !== null && (mode === 'existing' ? existing !== '' : label.trim() !== '' && username.trim() !== '' && secret !== '');

  const save = async (): Promise<void> => {
    if (site.siteId === null) return;
    setBusy(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        credential: mode === 'existing' ? { id: existing } : { create: { label: label.trim(), username: username.trim(), secret } },
        elevated: elevated === 'none' ? null : elevated,
      };
      if (autoAdd !== site.autoAdd) body['autoAdd'] = autoAdd;
      const r = await api.put<SetupResult>(`/api/nx-sites/${site.siteId}/setup`, body);
      setSecret('');
      setResult(r);
      await refresh();
    } catch (err) {
      setError(failure(err));
    } finally {
      setBusy(false);
    }
  };

  const option = (c: CredentialOption): React.JSX.Element => (
    <option key={c.id} value={c.id}>
      {c.label}
      {c.username ? ` — ${c.username}` : ''}
    </option>
  );
  const n = site.hosts.length;
  const state = siteState(site);

  return (
    <Drawer
      open
      onClose={onClose}
      title={`${result === null ? 'Set up' : 'Setting up'} NX site — ${site.name}`}
      subtitle={`${n} server${n === 1 ? '' : 's'}: ${site.hosts.map(labelOf).join(', ')}. One NX user serves them all: create it in NX first (view-only rights), then give it here.`}
    >
      <div className="form-narrow stack-tight">
        {error !== null && <Banner tone="fault">{error}</Banner>}
        {site.siteId === null && (
          <Banner tone="warn" title="These agents do not say which NX site they are on">
            An older agent sends the site’s name only. Update the agents, then come back; until then use Change logins on each host.
          </Banner>
        )}
        {result === null ? (
          <>
            <Banner tone={state.tone === 'ok' ? 'ok' : state.tone} title="Now">
              {state.text}
            </Banner>
            <Field id="nx-site-mode" label="Monitoring login (used every minute by every server)">
              <div className="row-tight">
                <label className="row-tight">
                  <input type="radio" name="nx-site-mode" checked={mode === 'existing'} disabled={credentials?.length === 0} onChange={() => setMode('existing')} />
                  A saved login
                </label>
                <label className="row-tight">
                  <input type="radio" name="nx-site-mode" checked={mode === 'new'} onChange={() => setMode('new')} />
                  A new login
                </label>
              </div>
            </Field>
            {mode === 'existing' ? (
              <Field id="nx-site-existing" label="Saved login" hint="From Settings › Credentials. Only usernames and passwords are offered.">
                <select id="nx-site-existing" value={existing} onChange={(e) => setExisting(e.target.value)}>
                  <option value="">Choose…</option>
                  {credentials?.map(option)}
                </select>
              </Field>
            ) : (
              <>
                <Field id="nx-site-label" label="Name for the credential" hint="How it appears under Settings › Credentials.">
                  <input id="nx-site-label" value={label} maxLength={120} onChange={(e) => setLabel(e.target.value)} />
                </Field>
                <Field id="nx-site-username" label="NX username" hint="The NX user you created: Site Health Viewers + Viewers is enough for monitoring.">
                  <input id="nx-site-username" value={username} autoComplete="off" onChange={(e) => setUsername(e.target.value)} />
                </Field>
                <Field id="nx-site-secret" label="NX password" hint="Sealed on save. It cannot be shown again, only replaced.">
                  <input id="nx-site-secret" type="password" value={secret} autoComplete="new-password" onChange={(e) => setSecret(e.target.value)} />
                </Field>
              </>
            )}
            <Field
              id="nx-site-elevated"
              label="Elevated login (NX actions only)"
              hint="A Power User, handed to an agent only for an NX action you ask for, then forgotten. The same login for both is fine for a single-login setup."
            >
              <select id="nx-site-elevated" value={elevated} onChange={(e) => setElevated(e.target.value)}>
                <option value="none">None — actions use the monitoring login</option>
                <option value="same">The same login</option>
                {credentials?.map(option)}
              </select>
            </Field>
            <label className="row-tight" htmlFor="nx-site-auto-add">
              <input id="nx-site-auto-add" type="checkbox" checked={autoAdd} onChange={(e) => setAutoAdd(e.target.checked)} />
              <span>
                Auto-add cameras from NX
                <span className="small muted"> — every camera the site’s servers list now, and each new one NX announces, each under the server NX has it on. Off adds nothing and removes nothing.</span>
              </span>
            </label>
            <div className="row-tight">
              <Button variant="primary" icon={<Save size={14} />} busy={busy} disabled={!ready} onClick={() => void save()}>
                {`Set up ${n} server${n === 1 ? '' : 's'}`}
              </Button>
              <span className="small muted">All or nothing: if one server cannot take it, none is changed.</span>
            </div>
          </>
        ) : (
          <>
            <Banner tone="ok" title={`${result.servers.length} server${result.servers.length === 1 ? '' : 's'} now log in as “${result.credentialLabel}”`}>
              {result.autoAdd === undefined
                ? 'Auto-add was left as it was.'
                : result.autoAdd.on
                  ? `Auto-add on: ${result.autoAdd.added.length} camera${result.autoAdd.added.length === 1 ? '' : 's'} added now` +
                    (result.autoAdd.held.length > 0 ? `, ${result.autoAdd.held.length} held back for you to link (see NX cameras)` : '') +
                    (result.autoAdd.skipped.length > 0 ? `. ${result.autoAdd.skipped.join('. ')}` : '.')
                  : 'Auto-add off; cameras already added stay.'}
            </Banner>
            <strong className="small">{allIn ? 'Every server logs in.' : 'Each agent tries the login at its next report, within a minute:'}</strong>
            <ul className="check-list">
              {site.hosts.map((h) => {
                const s = loginSummary(h);
                return (
                  <li key={h.id}>
                    {loggedIn(h) ? <Check size={14} className="text-ok" aria-label="logs in" /> : <span className="small muted">…</span>}
                    <span>
                      <span className="cell-strong">{labelOf(h)}</span>
                      <span className={`small ${s.problem ? 'text-warn' : 'muted'}`}> · {s.text}</span>
                    </span>
                  </li>
                );
              })}
            </ul>
            <div className="row-tight">
              <Button onClick={onClose}>Done</Button>
              {!allIn && <span className="small muted">Checking every ten seconds. You can close this; the Hosts page shows the same.</span>}
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
}
